import { SPECIAL_EVENT_TEMPLATE_ID, type StoreName } from '@bluebrick/discounts-service-module'
import { isFinite } from '@bluebrick/saas-ui'
import { isAxiosError } from 'axios'
import humanizeDuration from 'humanize-duration'
import { encode, decode } from 'js-base64'
import { camelCase, get, isArray, isEqual, isObject, sample, snakeCase, transform, uniq } from 'lodash-es'
import { type DateTime, Duration, Interval } from 'luxon'
import numbro from 'numbro'
import { type CamelCasedPropertiesDeep, type SnakeCasedPropertiesDeep } from 'type-fest'
import { dateTimeFormattedShort, dateTimeFormattedShortWithTime } from '@/common/dateUtils'
import { type TelemetryTransaction } from '@/features/telemetry/types'
import {
	Portal,
	type EventData,
	EventType,
	type TimelineEvent,
	type WithDates,
	type Note,
	UNASSIGNED_PRODUCT_NAME,
	NONE_PRODUCT_NAME,
	type Product,
	type Actions
} from '@/types'

type WidthProductName =
	| {
			product_name?: string
	  }
	| { productName?: string }

export type ElementRef = HTMLElement | null

export function isNil<T = unknown>(value: T): value is NonNullable<T> {
	return value === null || value === undefined
}

export function trim(text?: string): string {
	return (text ?? '').trim()
}

export function getSortAlphabeticallyByFieldComparator<T>(fieldName: keyof T, reverse = false): (a: T, b: T) => number {
	return (a, b) =>
		reverse
			? trim(String(b[fieldName] ?? '')).localeCompare(trim(String(a[fieldName] ?? '')))
			: trim(String(a[fieldName] ?? '')).localeCompare(trim(String(b[fieldName] ?? '')))
}

export function getDifferenceInDays(dateA: DateTime, dateB: DateTime): number {
	return Math.abs(dateA.toUTC().diff(dateB.toUTC(), 'days').days)
}

export function getDifferenceInHours(dateA: DateTime, dateB: DateTime): number {
	return Math.abs(dateA.toUTC().diff(dateB.toUTC(), 'hours').hours)
}

export function getDuration(dateA: DateTime, dateB: DateTime) {
	if (!dateA?.isValid || !dateB?.isValid) {
		return null
	}

	const diff = dateA.toUTC().diff(dateB.toUTC(), ['days', 'hours', 'minutes'])

	if (diff.days < 0 || diff.hours < 0 || diff.minutes < 0) {
		return diff.negate()
	}

	return diff
}

export function getEventEndDate(startDate: DateTime, length: number) {
	return startDate.plus({ days: length })
}

export function formatCurrency(value: number) {
	return numbro(value).formatCurrency({ thousandSeparated: true, spaceSeparated: true, mantissa: 2 })
}

export function formatUnits(value: number) {
	return numbro(value).format({ thousandSeparated: true }).replaceAll(',', ' ')
}

export const StoreToPortalMap = new Map<StoreName, Portal>([
	['steam', Portal.STEAM],
	['playstation_america', Portal.PLAYSTATION],
	['playstation_europe', Portal.PLAYSTATION],
	['playstation_asia', Portal.PLAYSTATION],
	['playstation_japan', Portal.PLAYSTATION],
	['playstation_unknown', Portal.PLAYSTATION],
	['epic', Portal.EPIC],
	['gog', Portal.GOG],
	['humble', Portal.HUMBLE],
	['microsoft', Portal.MICROSOFT],
	['nintendo_switch_europe_australia', Portal.NINTENDO],
	['nintendo_switch_americas', Portal.NINTENDO],
	['nintendo_switch_china', Portal.NINTENDO],
	['nintendo_switch_korea', Portal.NINTENDO],
	['nintendo_switch_taiwan_hong_kong', Portal.NINTENDO],
	['nintendo_switch_japan', Portal.NINTENDO],
	['nintendo_switch_unknown', Portal.NINTENDO],
	['nintendo_switch_2_europe_australia', Portal.NINTENDO],
	['nintendo_switch_2_americas', Portal.NINTENDO],
	['nintendo_switch_2_china', Portal.NINTENDO],
	['nintendo_switch_2_korea', Portal.NINTENDO],
	['nintendo_switch_2_taiwan_hong_kong', Portal.NINTENDO],
	['nintendo_switch_2_japan', Portal.NINTENDO],
	['nintendo_switch_2_unknown', Portal.NINTENDO],
	['nintendo_other_europe_australia', Portal.NINTENDO],
	['nintendo_other_americas', Portal.NINTENDO],
	['nintendo_other_taiwan_hong_kong', Portal.NINTENDO],
	['nintendo_other_korea', Portal.NINTENDO],
	['nintendo_other_japan', Portal.NINTENDO],
	['nintendo_other_unknown', Portal.NINTENDO],
	['nintendo_other_china', Portal.NINTENDO],
	['nintendo_other_southeast_asia', Portal.NINTENDO],
	['nintendo_switch_southeast_asia', Portal.NINTENDO],
	['nintendo_switch_2_southeast_asia', Portal.NINTENDO],
	['meta_rift', Portal.META],
	['meta_quest', Portal.META],
	['apple', Portal.APPLE],
	['google', Portal.GOOGLE]
])

export const portalFunctionalities = ['manualEvents', 'discountDepth', 'scheduling', 'pastScraper', 'eventPerformance'] as const
export type PortalFunctionality = (typeof portalFunctionalities)[number]

const allFunctionalities: Set<PortalFunctionality> = new Set(['manualEvents', 'discountDepth', 'scheduling', 'pastScraper', 'eventPerformance'])
const manualFunctionalities: Set<PortalFunctionality> = new Set(['manualEvents'])

export const StoreToPortalFunctionality = new Map<StoreName, Set<PortalFunctionality>>([
	['steam', allFunctionalities],
	['playstation_america', manualFunctionalities],
	['playstation_europe', manualFunctionalities],
	['playstation_asia', manualFunctionalities],
	['playstation_japan', manualFunctionalities],
	['epic', manualFunctionalities],
	['gog', manualFunctionalities],
	['humble', manualFunctionalities],
	['microsoft', manualFunctionalities],
	['nintendo_switch_europe_australia', allFunctionalities],
	['nintendo_switch_americas', allFunctionalities],
	['nintendo_switch_japan', allFunctionalities],
	['nintendo_switch_china', manualFunctionalities],
	['nintendo_switch_korea', allFunctionalities],
	['nintendo_switch_taiwan_hong_kong', allFunctionalities],
	['nintendo_switch_southeast_asia', allFunctionalities],
	['nintendo_switch_unknown', allFunctionalities],
	['nintendo_switch_2_europe_australia', allFunctionalities],
	['nintendo_switch_2_americas', allFunctionalities],
	['nintendo_switch_2_japan', allFunctionalities],
	['nintendo_switch_2_china', manualFunctionalities],
	['nintendo_switch_2_korea', allFunctionalities],
	['nintendo_switch_2_taiwan_hong_kong', allFunctionalities],
	['nintendo_switch_2_southeast_asia', allFunctionalities],
	['nintendo_switch_2_unknown', allFunctionalities],
	['nintendo_other_europe_australia', allFunctionalities],
	['nintendo_other_americas', allFunctionalities],
	['nintendo_other_japan', allFunctionalities],
	['nintendo_other_china', manualFunctionalities],
	['nintendo_other_korea', allFunctionalities],
	['nintendo_other_taiwan_hong_kong', allFunctionalities],
	['nintendo_other_southeast_asia', allFunctionalities],
	['nintendo_other_unknown', allFunctionalities],
	['meta_rift', manualFunctionalities],
	['meta_quest', manualFunctionalities],
	['apple', manualFunctionalities],
	['google', manualFunctionalities]
])

export const StoreToHumanNameMap = new Map<StoreName, string>([
	['steam', 'Steam'],
	['playstation_america', 'PlayStation'],
	['playstation_europe', 'PlayStation'],
	['playstation_asia', 'PlayStation'],
	['playstation_japan', 'PlayStation'],
	['epic', 'Epic'],
	['gog', 'GOG'],
	['humble', 'Humble'],
	['microsoft', 'Microsoft'],
	['nintendo_switch_europe_australia', 'Nintendo'],
	['nintendo_switch_americas', 'Nintendo'],
	['nintendo_switch_taiwan_hong_kong', 'Nintendo'],
	['nintendo_switch_southeast_asia', 'Nintendo'],
	['nintendo_switch_korea', 'Nintendo'],
	['nintendo_switch_japan', 'Nintendo'],
	['nintendo_switch_china', 'Nintendo'],
	['nintendo_switch_unknown', 'Nintendo'],
	['nintendo_switch_2_americas', 'Nintendo'],
	['nintendo_switch_2_europe_australia', 'Nintendo'],
	['nintendo_switch_2_taiwan_hong_kong', 'Nintendo'],
	['nintendo_switch_2_southeast_asia', 'Nintendo'],
	['nintendo_switch_2_korea', 'Nintendo'],
	['nintendo_switch_2_japan', 'Nintendo'],
	['nintendo_switch_2_china', 'Nintendo'],
	['nintendo_switch_2_unknown', 'Nintendo'],
	['nintendo_other_americas', 'Nintendo'],
	['nintendo_other_europe_australia', 'Nintendo'],
	['nintendo_other_taiwan_hong_kong', 'Nintendo'],
	['nintendo_other_southeast_asia', 'Nintendo'],
	['nintendo_other_korea', 'Nintendo'],
	['nintendo_other_japan', 'Nintendo'],
	['nintendo_other_china', 'Nintendo'],
	['nintendo_other_unknown', 'Nintendo'],
	['meta_rift', 'Meta'],
	['meta_quest', 'Meta'],
	['apple', 'Apple'],
	['google', 'Google']
])

export function isEventType(event: EventData, typeOrTypes: EventType | EventType[]) {
	if (Array.isArray(typeOrTypes)) {
		return typeOrTypes.includes(event.event_type)
	}

	return event.event_type === typeOrTypes
}

/*
	TODO: Define local computed event type (https://indiebi.atlassian.net/browse/DA-193)
	and use it here in the type guards
	#local-type
*/

//TODO: #local-type
export function isCustomerCreatedEvent(event: EventData) {
	return isDiscountEvent(event) && 'id' in event && !!event.id
}

// All scraped discounts and user created
//TODO: #local-type
export function isDiscountEvent(event: EventData) {
	return !!event.discount_event_type && !!event.event_origin && !isNil(event.triggers_cooldown) && !isOpportunityEvent(event)
}

// store opportunity and calculated opportunity
//TODO: #local-type
export function isOpportunityEvent(event: EventData) {
	return !!event.opportunity_type
}

// store opportunity
//TODO: #local-type
export function isStoreOpportunityEvent(event: EventData) {
	return isOpportunityEvent(event) && event.opportunity_type === 'store'
}

//TODO: #local-type
export function isCooldownEvent(event: EventData) {
	return !!event.cooldown_direction
}

export function getEventLength(event: EventData): number | undefined {
	if (event.start_date?.isValid && event.end_date?.isValid) {
		return getDifferenceInDays(event.start_date, event.end_date)
	}

	return
}

export function getRangeDescription(start: DateTime, end: DateTime) {
	if (!start?.isValid || !end?.isValid) {
		return ''
	}

	return `${dateTimeFormattedShortWithTime(start, true)} - ${dateTimeFormattedShortWithTime(end, true)}`
}

export function getRangeDescriptionWithoutTime(start: DateTime, end: DateTime) {
	if (!start?.isValid || !end?.isValid) {
		return ''
	}

	return `${dateTimeFormattedShort(start)} - ${dateTimeFormattedShort(end)}`
}

export function getEventRecommendedLength(event: EventData): number | undefined {
	if (event.recommended_start_date?.isValid && event.recommended_end_date?.isValid) {
		return getDifferenceInDays(event.recommended_start_date, event.recommended_end_date)
	}

	return
}

export function hasNonOptimalLength(event: EventData) {
	if (!isDiscountEvent(event)) {
		return false
	}

	const length = getEventLength(event)
	const recommendedLength = getEventRecommendedLength(event)

	return Number.isFinite(length) && Number.isFinite(recommendedLength) && length !== recommendedLength
}

export function hasNonOptimalDepth(event: EventData) {
	if (!isDiscountEvent(event)) {
		return false
	}

	return (
		Number.isFinite(event.discount_depth) &&
		Number.isFinite(event.recommended_discount_depth) &&
		event.discount_depth !== event.recommended_discount_depth
	)
}

export function isNonOptimalEvent(event: EventData) {
	if (!isDiscountEvent(event)) {
		return false
	}

	return hasNonOptimalLength(event) || hasNonOptimalDepth(event)
}

export function eventDataToTimelineEvent(event: EventData, resource: string, storeName: StoreName, timeZone = 'local'): TimelineEvent {
	return {
		id: event.id ?? undefined,
		editable: event.event_type === EventType.Discount,
		resource,
		start: event.start_date.setZone(timeZone),
		end: event.end_date.setZone(timeZone),
		rawEvent: {
			...event,
			start_date: event.start_date.setZone(timeZone),
			end_date: event.end_date.setZone(timeZone),
			storeName
		}
	}
}

export function hasOverlap(eventA: WithDates, eventB: WithDates) {
	if (!eventA.start_date?.isValid || !eventA.end_date?.isValid || !eventB.start_date?.isValid || !eventB.end_date?.isValid) {
		return false
	}

	const intervalA = Interval.fromDateTimes(eventA.start_date.toUTC(), eventA.end_date.toUTC())
	const intervalB = Interval.fromDateTimes(eventB.start_date.toUTC(), eventB.end_date.toUTC())

	return intervalA.isValid && intervalB.isValid && intervalA.overlaps(intervalB)
}

export function haveSameDates(eventA: WithDates, eventB: WithDates) {
	if (!eventA.start_date?.isValid || !eventA.end_date?.isValid || !eventB.start_date?.isValid || !eventB.end_date?.isValid) {
		return false
	}

	return eventA.start_date.toUTC().equals(eventB.start_date.toUTC()) && eventA.end_date.toUTC().equals(eventB.end_date.toUTC())
}

export function isEventCancellationError(error: unknown, abortSignal?: AbortSignal) {
	return isAxiosError(error) && error.code === 'ERR_CANCELED' && abortSignal?.aborted
}

export function getSortedByDate(events: EventData[]): EventData[] {
	return events.sort((eventA, eventB) => {
		if (!eventA.start_date.isValid || !eventB.start_date.isValid) {
			return 0
		}

		return eventA.start_date.toMillis() - eventB.start_date.toMillis()
	})
}

export const sortByHumanNameComparator = getSortAlphabeticallyByFieldComparator('human_name')
export const sortByCamelHumanNameComparator = getSortAlphabeticallyByFieldComparator('humanName')
export const sortByProductNameComparator = (a: WidthProductName, b: WidthProductName) => {
	const nameA = get(a, 'product_name') ?? get(a, 'productName') ?? UNASSIGNED_PRODUCT_NAME
	const nameB = get(b, 'product_name') ?? get(b, 'productName') ?? UNASSIGNED_PRODUCT_NAME

	if (nameA === UNASSIGNED_PRODUCT_NAME) {
		return 1
	}

	if (nameB === UNASSIGNED_PRODUCT_NAME) {
		return -1
	}

	return nameA.localeCompare(nameB)
}

export function isEventVisible(
	event: EventData,
	hashToEventMap: Record<string, EventData>,
	isShowingNotesOnly: boolean,
	isShowingScrapedOnly: boolean
) {
	if (!isShowingNotesOnly && !isShowingScrapedOnly) {
		// Show all events if the filtering flags are disabled
		return true
	}

	if (isCooldownEvent(event)) {
		if (isShowingScrapedOnly) {
			return event.source_event_hash && isEventScraped(hashToEventMap[event.source_event_hash])
		}

		if (isShowingNotesOnly) {
			return event.source_event_hash && hasEventNotes(hashToEventMap[event.source_event_hash])
		}

		return true
	}

	function isDiscountEventVisible(discountEvent: EventData) {
		return (!isShowingNotesOnly || hasEventNotes(discountEvent)) && (!isShowingScrapedOnly || isEventScraped(discountEvent))
	}

	if (isOpportunityEvent(event)) {
		// Hide all opportunities if any of the flags is set
		return false
	}

	if (isDiscountEvent(event)) {
		return isDiscountEventVisible(event)
	}

	// Hide everything else if any of the flags is set
	return false
}

export function encodeNotes(notes?: Note[]) {
	if (!notes) {
		return
	}

	return notes.map((note) => ({
		...note,
		text: encode(note.text)
	}))
}

function isBase64String(input: string) {
	return /^([A-Za-z0-9+/]{4})*([A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{2}==)?$/.test(input)
}

export function decodeNotes(notes?: Note[], shouldAnonymizeData?: boolean) {
	if (!notes) {
		return
	}

	return notes.map((note) => {
		if (note.text && isBase64String(note.text)) {
			try {
				const decodedText = decode(note.text)

				return {
					...note,
					text: shouldAnonymizeData ? 'Lorem ipsum dolor sot amet...' : decodedText
				}
			} catch {
				return note
			}
		}

		return note
	})
}

export function getNormalizedProductName(productName: string | undefined) {
	if (!productName || productName === NONE_PRODUCT_NAME) {
		return UNASSIGNED_PRODUCT_NAME
	}

	return productName
}

function addToProductHumanNameMapIfNotPresent(aMap: Record<string, string[]>, productName: string, humanName: string) {
	if (productName in aMap) {
		if (!aMap[productName].includes(humanName)) {
			aMap[productName].push(humanName)
		}
	} else {
		aMap[productName] = [humanName]
	}
}

/**
 * Normalizes names of SKUs received from the API by:
 * - using proper product name for SKUs unassigned to any product
 * - appending base SKU id to SKU's human name in case when there is more than one SKU with the same
 * name for given store/product
 * An optional mapping function can be used here to allow addtional processing of each SKU (to avoid
 * running multiple `.map` functions through full list of SKUs)
 */
export function normalizeSKUNames<T extends Product>(skus: T[], mappingFunction?: (sku: T) => T, shouldAnonymizeData?: boolean): T[] {
	/*
	These two maps (product name -> list of human names) are used to detect SKUs under given product
	which have the same human name. In such case we append SKU id to each of the repeated human names
	*/
	const allHumanNamesPerProduct: Record<string, string[]> = {}
	const duplicatedHumanNamesPerProduct: Record<string, string[]> = {}
	let hasFoundAnyDuplicates = false

	skus.forEach((skuData) => {
		skuData.product_name = getNormalizedProductName(skuData.product_name)

		if (shouldAnonymizeData) {
			if (skuData.product_name !== UNASSIGNED_PRODUCT_NAME) {
				skuData.product_name = getAnonymizedProductName(skuData.product_name)
			}

			skuData.human_name = getAnonymizedHumanName(skuData.product_name)
		}

		const sku = mappingFunction ? mappingFunction(skuData) : skuData

		if (sku.product_name && sku.product_name in allHumanNamesPerProduct && allHumanNamesPerProduct[sku.product_name].includes(sku.human_name)) {
			hasFoundAnyDuplicates = true

			addToProductHumanNameMapIfNotPresent(duplicatedHumanNamesPerProduct, sku.product_name, sku.human_name)
		}

		if (sku.product_name) {
			addToProductHumanNameMapIfNotPresent(allHumanNamesPerProduct, sku.product_name, sku.human_name)
		}
	})

	if (hasFoundAnyDuplicates) {
		// No better idea than to iterate the SKUs again an add "SKU id" suffix to those with duplicated names
		skus.forEach((sku) => {
			if (
				sku.product_name &&
				sku.product_name in duplicatedHumanNamesPerProduct &&
				duplicatedHumanNamesPerProduct[sku.product_name].includes(sku.human_name)
			) {
				sku.human_name = `${sku.human_name} (SKU: ${sku.base_sku_id})`
			}
		})
	}

	return skus
}

const UNCONVERTABLE_TYPE_CONSTRUCTORS = [Date, FormData] as const

const isConvertable = (value: unknown) =>
	isObject(value) && !UNCONVERTABLE_TYPE_CONSTRUCTORS.some((unconvertableTypeConstructor) => value instanceof unconvertableTypeConstructor)

const camelize = <T extends Record<string, unknown>>(obj: T) =>
	isConvertable(obj)
		? transform(obj, (result: Record<string, unknown>, value: unknown, key: string, target) => {
				const camelKey = isArray(target) ? key : camelCase(key)

				result[String(camelKey)] = camelize(value as Record<string, unknown>)
			})
		: obj

const snakize = (obj: Record<string, unknown>) =>
	isConvertable(obj)
		? transform(obj, (result: Record<string, unknown>, value: unknown, key: string, target) => {
				const snakeKey = isArray(target) ? key : snakeCase(key)

				result[String(snakeKey)] = snakize(value as Record<string, unknown>)
			})
		: obj

export function convertToCamelCase<T extends Record<string, unknown>>(input: T): CamelCasedPropertiesDeep<T> {
	return camelize(input) as CamelCasedPropertiesDeep<T>
}

export function convertToSnakeCase<T extends Record<string, unknown>>(input: T): SnakeCasedPropertiesDeep<T> {
	return snakize(input) as SnakeCasedPropertiesDeep<T>
}

/**
 * Returns a set of elements which are present in `setA`, but not in `setB` or are present
 * in `setB`, but not in `setA`.
 * Note: Can be replaced (once we bump Chromium version in Electron app) by native
 * `Set.symmetricDifference` which is available in Chrome 122+
 */
export function getSymmetricDifference<T>(setA: Set<T>, setB: Set<T>): Set<T> {
	const result = new Set<T>()

	for (const setAElement of setA) {
		if (!setB.has(setAElement)) {
			result.add(setAElement)
		}
	}

	for (const setBElement of setB) {
		if (!setA.has(setBElement)) {
			result.add(setBElement)
		}
	}

	return result
}

export function formatScore(score: number, fractionDigits = 1) {
	return score.toFixed(fractionDigits)
}

export function getScoreColor(score: number) {
	if (score > 90) {
		return 'success'
	}

	if (score > 60) {
		return 'warning'
	}

	return 'error'
}

export async function transactionSpanWrapper(fn: () => Promise<void>, spanName: string, transaction?: TelemetryTransaction) {
	const spanEventsWithCooldowns = transaction?.startSpan(spanName, 'loading events')

	await fn()
	spanEventsWithCooldowns?.end()
}

export function isEventScraped(event: EventData): boolean {
	return event?.event_origin === 'scraped'
}

export function hasEventNotes(event: EventData): boolean {
	return Boolean(event?.notes && event.notes.length > 0)
}

export function openLink(url: string) {
	window.open(url, '_blank')
}

export function getActionsColor(actions: Actions) {
	if (actions.unscheduledEvents.length > 0) {
		return 'error'
	}

	if (actions.unplannedEvents.length > 0) {
		return 'warning'
	}

	return 'success'
}

export function isSpecialEventTemplate<T extends { unique_sku_id: string }>(eventOrSKU: T) {
	return eventOrSKU.unique_sku_id === SPECIAL_EVENT_TEMPLATE_ID
}

// Stores anonymized product names with its original name and the anonymized one. This is to be concise with anonymizing.
const anonymizedProductNames: Map<string, string> = new Map()

// Anonymizes the given product name and saves the original-anonymized pair in anonymizedProductNames map, so that the anonymizing is concise.
export function getAnonymizedProductName(productName: string): string {
	const availableProductNames = [
		'Cybernetic Havoc',
		'Elderwild: Chronicles of the Forgotten',
		'Solar Tempest',
		'Galactic Warlords',
		'Ironclad: Legacy of the Titans',
		'Shadow Realm Odyssey',
		'Astral Nexus: Guardians of the Void',
		'Mystic Forge: Elemental Awakening',
		'Neon Vengeance',
		'Quantum Rift: Echoes of Time',
		'Voidborn: Echoes of the Abyss',
		'Titanfall Requiem',
		'Neurostorm: Rise of the Synths',
		'Starborn Dominion',
		'Wraithbound: Curse of the Ancients',
		'ExoGenesis: The Last Frontier',
		'Bloodline Ascension',
		'Chrono Shattered: Fractures of Fate',
		'Darkspire: Legends of the Lost'
	]

	if (anonymizedProductNames.has(productName)) {
		return anonymizedProductNames.get(productName) || availableProductNames[0]
	} else {
		const anonymizedName = sample(availableProductNames) || availableProductNames[0]

		anonymizedProductNames.set(productName, anonymizedName)

		return anonymizedName
	}
}

export function getAnonymizedHumanName(productName?: string) {
	return (productName || 'test') + ` (SKU: ${Math.round(Math.random() * 100000)})`
}

export function getAnonymizedEventName(originalEventName?: string): string | undefined {
	const safeNameRegExp = /^steam (spring|winter|autumn|summer) sale \d{4}$/

	if (!originalEventName || safeNameRegExp.test(originalEventName.trim().toLocaleLowerCase())) {
		return originalEventName
	}

	return 'Custom sale'
}

export function toBase64(text: string): string {
	const bytes = new TextEncoder().encode(text)
	const binary = Array.from(bytes, (b) => String.fromCharCode(b)).join('')

	return btoa(binary)
}

function fromBase64(base64Text: string): string {
	const binary = atob(base64Text)
	const bytes = new Uint8Array(binary.length)

	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i)
	}

	return new TextDecoder().decode(bytes)
}

type WithName = {
	name?: string | null | undefined
}

/**
 * Empties "name" field of given event and returns event's copy.
 * #event-names-vs-firewall
 */
export function stripName<T extends WithName>(event: T) {
	if (!event.name) {
		return event
	}

	return {
		...event,
		name: ''
	}
}

/**
 * Encodes given envent's name so that it's safe to be sent to API (without tripping the firewall).
 * #event-names-vs-firewall
 */
export function encodeName<T extends WithName>(event: T) {
	if (!event.name) {
		return event
	}

	return {
		...event,
		name: toBase64(event.name)
	}
}

/**
 * Decodes given envent's name back to original form.
 * #event-names-vs-firewall
 */
export function decodeName<T extends WithName>(event: T) {
	if (!event.name) {
		return event
	}

	return {
		...event,
		name: fromBase64(event.name ?? '')
	}
}

export function CSVDownload(csvContent: string, filename: string) {
	const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
	const link = document.createElement('a')
	const url = URL.createObjectURL(blob)

	link.setAttribute('href', url)
	link.setAttribute('download', `${filename}.csv`)
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}

/**
 * Formats duration in days to a text format. For example formatDuration(1.5) -> "1 day, 12 hours"
 */
export function formatDuration(durationInDays: number | undefined, roundToHours = true) {
	if (!isFinite(durationInDays)) {
		return ''
	}

	const duration = Duration.fromDurationLike({ milliseconds: durationInDays * 24 * 60 * 60 * 1000 })

	if (!duration.isValid) {
		return ''
	}

	if (roundToHours) {
		return humanizeDuration(Duration.fromObject({ hours: Math.round(duration.as('hours')) }).valueOf(), {
			language: 'en',
			units: ['d', 'h', 'm']
		})
	}

	return humanizeDuration(duration.valueOf(), {
		language: 'en',
		units: ['d', 'h', 'm']
	})
}

/**
 * Returns true if arrays have the same unique elements, regardless of the order or duplicates.
 * False otherwise.
 */
export function arraysHaveTheSameElements(arrayA: unknown[], arrayB: unknown[]) {
	return isEqual(uniq(arrayA).sort(), uniq(arrayB).sort())
}

export const portalToBackendLinkMap = new Map<Portal, string>([
	[Portal.STEAM, 'https://partner.steamgames.com/?ref=stebet.net'],
	[Portal.NINTENDO, 'https://ncms3.mng.nintendo.net/ncms3/login'],
	[Portal.PLAYSTATION, 'https://partners.playstation.net/'],
	[Portal.EPIC, 'https://dev.epicgames.com/portal/en-US/'],
	[Portal.META, 'https://developers.meta.com/horizon/'],
	[Portal.HUMBLE, 'https://www.humblebundle.com/login']
])

export function getPortalLink(storeName?: StoreName): string | undefined {
	if (!storeName) {
		return
	}

	const portal = StoreToPortalMap.get(storeName)

	if (!portal) {
		return
	}

	return portalToBackendLinkMap.get(portal)
}

export function haveSameName(eventA: WithName, eventB: WithName) {
	return eventA.name === eventB.name
}
